import { FC, useEffect, useState } from 'react';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import campaignsDashboardColumnsSettingsApplyButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaigns_dashboard/campaigns_dashboard_columns_settings_apply_button_click';
import campaignsDashboardColumnsSettingsModalShown from '@hh.ru/analytics-js-events/build/clickme/employer/campaigns_dashboard/campaigns_dashboard_columns_settings_modal_shown';
import {
    ActionBar,
    BottomSheet,
    Button,
    Cell,
    CellText,
    CheckableCard,
    Modal,
    NavBar,
    Radio,
    Text,
    useBreakpoint,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { getCampaignsTableColumns } from 'components/CampaignsDashboard/utils/utils';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';

import { ColumnsPreset, setColumnsPreset } from 'store/models/campaignsFilters';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'CampaignsFiltersPanel.SelectColumnsModal.title',
    applyButton: 'CampaignsFiltersPanel.SelectColumnsModal.applyButton',
    presets: {
        [ColumnsPreset.Important]: {
            title: 'CampaignsFiltersPanel.SelectColumnsModal.important',
            description: 'CampaignsFiltersPanel.SelectColumnsModal.important.description',
        },
        [ColumnsPreset.All]: {
            title: 'CampaignsFiltersPanel.SelectColumnsModal.all',
            description: 'CampaignsFiltersPanel.SelectColumnsModal.all.description',
        },
    },
};

interface SelectColumnsModalProps {
    visible: boolean;
    onClose: () => void;
}

export const SelectColumnsModal: FC<SelectColumnsModalProps> = ({ visible, onClose }) => {
    const columnsPreset = useSelector((state) => state.campaignsFilters.columnsPreset);
    const [columnsPresetDraft, setColumnsPresetDraft] = useState<ColumnsPreset>(columnsPreset);

    const trls = useTrls();
    const { isMobile } = useBreakpoint();
    const dispatch = useDispatch();

    const applyHandler = () => {
        localStorage.setItem('columnsPreset', columnsPresetDraft);
        dispatch(setColumnsPreset(columnsPresetDraft));

        campaignsDashboardColumnsSettingsApplyButtonClick({
            columns: JSON.stringify(getCampaignsTableColumns(columnsPresetDraft)),
        });

        onClose();
    };

    const onAfterExitHandler = () => {
        setColumnsPresetDraft(columnsPreset);
    };

    useEffect(() => {
        const savedColumnsPreset = localStorage.getItem('columnsPreset') as ColumnsPreset | null;

        if (savedColumnsPreset && Object.values(ColumnsPreset).includes(savedColumnsPreset)) {
            dispatch(setColumnsPreset(savedColumnsPreset));
            setColumnsPresetDraft(savedColumnsPreset);
        } else {
            dispatch(setColumnsPreset(ColumnsPreset.Important));
            setColumnsPresetDraft(ColumnsPreset.Important);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const content = (
        <ElementShownAnchor fn={campaignsDashboardColumnsSettingsModalShown}>
            <VSpacingContainer default={12}>
                {Object.values(ColumnsPreset).map((preset) => (
                    <CheckableCard
                        key={preset}
                        flexible
                        padding={16}
                        borderRadius={16}
                        toneHover={true}
                        type="radio"
                        name={preset}
                        checked={columnsPresetDraft === preset}
                        onChange={() => setColumnsPresetDraft(preset)}
                        data-qa={`campaigns-columns-preset-${preset}`}
                    >
                        <Cell align="top" left={<Radio checked={columnsPresetDraft === preset} readOnly />}>
                            <CellText>{trls(TrlKeys.presets[preset].title)}</CellText>
                            <CellText type="subtitle">{trls(TrlKeys.presets[preset].description)}</CellText>
                        </Cell>
                    </CheckableCard>
                ))}
            </VSpacingContainer>
        </ElementShownAnchor>
    );

    const footer = (
        <ActionBar
            type="vertical"
            primaryActions={
                <Button
                    stretched
                    mode="primary"
                    style="accent"
                    onClick={applyHandler}
                    data-qa="campaigns-columns-modal-apply-button"
                >
                    {trls(TrlKeys.applyButton)}
                </Button>
            }
        />
    );

    if (isMobile) {
        return (
            <BottomSheet
                visible={visible}
                onClose={onClose}
                height="content"
                footer={footer}
                header={
                    <NavBar>
                        <NavBar.Pane>
                            <NavBar.Actions
                                right={<CrossOutlinedSize24 padding={8} initialColor="secondary" onClick={onClose} />}
                            >
                                <NavBar.TitleContainer
                                    title={<Text typography="title-5-semibold">{trls(TrlKeys.title)}</Text>}
                                />
                            </NavBar.Actions>
                        </NavBar.Pane>
                    </NavBar>
                }
                data-qa="campaigns-columns-bottom-sheet"
                onAfterExit={onAfterExitHandler}
            >
                {content}
            </BottomSheet>
        );
    }

    return (
        <Modal
            size="small"
            visible={visible}
            onClose={onClose}
            header={
                <MagritteModalHeader
                    title={trls(TrlKeys.title)}
                    actions={<CrossOutlinedSize24 initialColor="secondary" onClick={onClose} />}
                />
            }
            footer={footer}
            data-qa="campaigns-columns-modal"
            onAfterExit={onAfterExitHandler}
        >
            {content}
        </Modal>
    );
};
